/**
 * Contract for a metrics provider.
 *
 * A metrics client decouples event tracking call sites from the underlying
 * provider (Segment, Sentry, ...). Concrete clients extend this base class and
 * implement the methods they support. `group` and `flush` are optional: the
 * defaults here are safe no-ops so call sites never need to feature-detect.
 *
 * @abstract
 */
class IMetricsClient {
  /**
   * @param {object} [options]
   * @param {(error: Error) => void} [options.onError] Optional hook invoked when
   *   an underlying provider SDK call throws or rejects. Provider failures are
   *   never propagated into the caller (a metrics call must not break business
   *   logic); this hook keeps them observable. It should not itself throw.
   */
  constructor({ onError } = {}) {
    this.onError = typeof onError === "function" ? onError : null;
  }

  /**
   * Record an action a user performed.
   *
   * @param {string|null} userId Easil user id, or null when the user has
   *   already been identified out of band (e.g. browser Segment / Sentry scope).
   * @param {string} eventName Name of the event to record.
   * @param {Object} [properties] Optional key/value event metadata.
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  track(userId, eventName, properties = {}) {
    throw new Error("IMetricsClient.track() must be implemented");
  }

  /**
   * Associate traits with a user.
   *
   * @param {string} userId Easil user id.
   * @param {Object} [traits] Optional key/value user traits.
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  identify(userId, traits = {}) {
    throw new Error("IMetricsClient.identify() must be implemented");
  }

  /**
   * Associate a user with a group (e.g. team / organisation). Optional.
   *
   * @param {string} groupId Group id.
   * @param {string} userId Easil user id.
   * @param {Object} [groupTraits] Optional key/value group traits.
   * @returns {void}
   */
  // eslint-disable-next-line no-unused-vars
  group(groupId, userId, groupTraits = {}) {}

  /**
   * Flush any buffered events. Optional; resolves immediately by default.
   *
   * @returns {Promise<void>}
   */
  async flush() {}

  /**
   * Invoke a provider SDK call defensively. Synchronous throws and rejected
   * promises are caught and routed to `onError`, so a failing metrics provider
   * can never break the calling code path. Returns the SDK result (or, for an
   * async call, a promise that always resolves).
   *
   * @protected
   * @param {Function} fn Thunk that performs the provider SDK call.
   */
  safeInvoke(fn) {
    try {
      const result = fn();
      if (result && typeof result.then === "function") {
        return result.then(undefined, error => this.reportError(error));
      }
      return result;
    } catch (error) {
      this.reportError(error);
    }
  }

  /**
   * @protected
   * @param {Error} error
   */
  reportError(error) {
    if (!this.onError) return;
    try {
      this.onError(error);
    } catch {
      // The onError hook is the last line of defence; if it throws too, swallow
      // it so a metrics failure can never reach the caller (sync) or surface as
      // an unhandled rejection (async).
      return;
    }
  }
}

module.exports = IMetricsClient;
